// The instruments a new track can be, as the picker shows them: a name a
// musician would use, a line on what it's for, and a few notes to hear it by.
// One entry per synth preset (lib/synth.ts), in the picker's order.

import type { InstrumentId } from "@shared/instruments.js";
import type { MidiNoteEvent } from "@web-shared/lib/audio-engine";

export interface InstrumentEntry {
  id: InstrumentId;
  name: string;
  blurb: string;
  /** A default track name for a lane that plays it. */
  trackName: string;
}

export const INSTRUMENT_CATALOG: readonly InstrumentEntry[] = [
  { id: "keys", name: "Keys", blurb: "Soft electric piano for chords", trackName: "Keys" },
  { id: "kit", name: "Drums", blurb: "A drum kit, programmed on a step grid", trackName: "Drums" },
  { id: "bass", name: "Bass", blurb: "Round, punchy low end", trackName: "Bass" },
  { id: "pluck", name: "Pluck", blurb: "Short and bright, for riffs", trackName: "Pluck" },
  { id: "pad", name: "Pad", blurb: "Slow, wide, sustained", trackName: "Pad" },
  { id: "lead", name: "Lead", blurb: "A square-wave lead line", trackName: "Lead" },
  { id: "sine", name: "Sine", blurb: "A pure tone — plain and clean", trackName: "Synth" },
];

export function instrumentEntry(id: string | undefined): InstrumentEntry | undefined {
  return INSTRUMENT_CATALOG.find((e) => e.id === id);
}

/** A short phrase to audition an instrument by, in seconds from now. */
export function previewPhrase(id: InstrumentId): MidiNoteEvent[] {
  const n = (pitch: number, at: number, len: number, velocity = 100): MidiNoteEvent => ({
    pitch,
    startSec: at,
    durationSec: len,
    velocity,
  });
  switch (id) {
    case "kit":
      // Kick, hat, snare, hat — General MIDI drum pitches.
      return [n(36, 0, 0.2), n(42, 0.25, 0.1, 80), n(38, 0.5, 0.2), n(42, 0.75, 0.1, 80), n(36, 1, 0.2)];
    case "bass":
      return [n(36, 0, 0.3), n(36, 0.375, 0.15), n(43, 0.5, 0.3), n(41, 0.875, 0.4)];
    case "pad":
      return [n(57, 0, 1.4, 80), n(60, 0, 1.4, 80), n(64, 0, 1.4, 80)];
    case "keys":
      return [n(60, 0, 0.9, 90), n(64, 0, 0.9, 80), n(67, 0, 0.9, 80), n(71, 0.45, 0.6, 70)];
    default:
      return [n(60, 0, 0.2), n(64, 0.2, 0.2), n(67, 0.4, 0.2), n(72, 0.6, 0.4)];
  }
}
